import type { Language } from './i18n';

type InteractiveCopy = {
  plan: {
    label: string;
    title: string;
    intro: string;
    choose: string;
    services: { id: string; title: string; description: string }[];
    summary: string;
    empty: string;
    quote: string;
    quoteNote: string;
    currency: string;
    action: string;
    messagePrefix: string;
  };
  faq: {
    label: string;
    title: string;
    search: string;
    noResults: string;
    items: { question: string; answer: string }[];
  };
};

export const interactiveCopy: Record<Language, InteractiveCopy> = {
  pl: {
    plan: {
      label: 'Wycena', title: 'Zacznij od tego, czego potrzebujesz.',
      intro: 'Wybierz obszary projektu. To punkt wyjścia do rozmowy, nie automatyczna oferta.',
      choose: 'Zakres projektu',
      services: [
        { id: 'website', title: 'Strona internetowa', description: 'Strona firmy, formularze i treść, którą można wygodnie aktualizować.' },
        { id: 'app', title: 'Aplikacja dla firmy', description: 'Panel lub narzędzie dopasowane do codziennej pracy zespołu.' },
        { id: 'automation', title: 'Automatyzacja', description: 'Połączenie narzędzi i uporządkowanie powtarzalnych zadań.' },
        { id: 'ai', title: 'Funkcje AI', description: 'Pomoc w analizie, tłumaczeniu lub przygotowaniu treści pod kontrolą człowieka.' }
      ],
      summary: 'Twój zakres', empty: 'Wybierz przynajmniej jeden obszar.', quote: 'Wycena indywidualna',
      quoteNote: 'Kwotę i termin podam po poznaniu zakresu. Niczego nie zamawiasz na tej stronie.',
      currency: 'Wycena w PLN', action: 'Porozmawiajmy o wycenie', messagePrefix: 'Interesuje mnie:'
    },
    faq: {
      label: 'Pytania', title: 'Konkretne odpowiedzi.', search: 'Szukaj pytania', noResults: 'Nie znaleziono pytania. Napisz do mnie poniżej.',
      items: [
        { question: 'Jak powstaje wycena?', answer: 'Najpierw ustalamy cel, zakres i istniejące narzędzia. Potem otrzymasz propozycję rozwiązania oraz wycenę w PLN. Sam wybór opcji na tej stronie nie jest zamówieniem.' },
        { question: 'Czy trzeba od razu budować cały system?', answer: 'Nie. Możemy zacząć od jednego konkretnego procesu i sprawdzić go w praktyce, zanim rozszerzymy rozwiązanie.' },
        { question: 'Czy da się połączyć rozwiązanie z naszymi narzędziami?', answer: 'To sprawdzam na początku projektu. Możliwości zależą od dostępu, API i zasad bezpieczeństwa używanych usług.' },
        { question: 'Czy AI podejmuje decyzje za firmę?', answer: 'Nie powinno decydować o cenie, terminie ani zobowiązaniach bez ustalonych reguł i odpowiedniej kontroli człowieka.' }
      ]
    }
  },
  en: {
    plan: {
      label: 'Pricing', title: 'Start with what you need.',
      intro: 'Select the areas of your project. This starts a conversation; it is not an automatic offer.',
      choose: 'Project scope',
      services: [
        { id: 'website', title: 'Website', description: 'A business site, forms and content you can update.' },
        { id: 'app', title: 'Business application', description: 'A dashboard or tool shaped around your team’s daily work.' },
        { id: 'automation', title: 'Automation', description: 'Connected tools and fewer repetitive tasks.' },
        { id: 'ai', title: 'AI features', description: 'Assistance with analysis, translation or drafts, with human oversight.' }
      ],
      summary: 'Your scope', empty: 'Choose at least one area.', quote: 'Custom quote',
      quoteNote: 'I will confirm the price and timeline after discussing the scope. Nothing is ordered here.',
      currency: 'Quote currency', action: 'Discuss a quote', messagePrefix: 'I am interested in:'
    },
    faq: {
      label: 'Questions', title: 'Straight answers.', search: 'Search questions', noResults: 'No matching question. Get in touch below.',
      items: [
        { question: 'How is a project priced?', answer: 'We discuss the goal, scope and tools already in use. Then I send a proposal and a quote in PLN. Indicative USD equivalents are shown for reference. Selecting options here does not place an order.' },
        { question: 'Do we need to build the whole system at once?', answer: 'No. We can start with one useful process and test it in real work before expanding.' },
        { question: 'Can it connect to our current tools?', answer: 'I check this early. It depends on available access, APIs and the security rules of each service.' },
        { question: 'Will AI make business decisions for us?', answer: 'It should not decide pricing, dates or commitments without agreed rules and appropriate human oversight.' }
      ]
    }
  },
  uk: {
    plan: {
      label: 'Оцінка вартості', title: 'Почніть із того, що вам потрібно.',
      intro: 'Оберіть напрями проєкту. Це початок розмови, а не автоматична пропозиція.',
      choose: 'Обсяг проєкту',
      services: [
        { id: 'website', title: 'Вебсайт', description: 'Сайт компанії, форми та контент, який зручно оновлювати.' },
        { id: 'app', title: 'Робочий застосунок', description: 'Панель або інструмент для щоденної роботи команди.' },
        { id: 'automation', title: 'Автоматизація', description: 'Поєднання інструментів і менше повторюваних задач.' },
        { id: 'ai', title: 'Функції AI', description: 'Допомога з аналізом, перекладом або чернетками під контролем людини.' }
      ],
      summary: 'Ваш обсяг', empty: 'Оберіть хоча б один напрям.', quote: 'Індивідуальний розрахунок',
      quoteNote: 'Вартість і термін повідомлю після обговорення обсягу. Тут ви нічого не замовляєте.',
      currency: 'Оцінка в PLN', action: 'Обговорити вартість', messagePrefix: 'Мене цікавить:'
    },
    faq: {
      label: 'Запитання', title: 'Конкретні відповіді.', search: 'Знайти запитання', noResults: 'Запитання не знайдено. Напишіть мені нижче.',
      items: [
        { question: 'Як формується вартість?', answer: 'Спершу обговорюємо мету, обсяг і наявні інструменти. Потім ви отримаєте пропозицію та вартість у PLN. Вибір опцій тут не є замовленням.' },
        { question: 'Чи потрібно відразу будувати всю систему?', answer: 'Ні. Можемо почати з одного процесу й перевірити його на практиці, перш ніж розширювати рішення.' },
        { question: 'Чи можна поєднати рішення з нашими інструментами?', answer: 'Це перевіряю на початку. Можливості залежать від доступу, API та правил безпеки сервісів.' },
        { question: 'Чи прийматиме AI бізнес-рішення замість нас?', answer: 'AI не має визначати ціну, терміни чи зобов’язання без погоджених правил і належного контролю людини.' }
      ]
    }
  },
  ru: {
    plan: {
      label: 'Оценка стоимости', title: 'Начните с того, что вам нужно.',
      intro: 'Выберите направления проекта. Это начало разговора, а не автоматическое предложение.',
      choose: 'Объём проекта',
      services: [
        { id: 'website', title: 'Сайт', description: 'Сайт компании, формы и контент, который удобно обновлять.' },
        { id: 'app', title: 'Рабочее приложение', description: 'Панель или инструмент для повседневной работы команды.' },
        { id: 'automation', title: 'Автоматизация', description: 'Связь инструментов и меньше повторяющихся задач.' },
        { id: 'ai', title: 'Функции AI', description: 'Помощь с анализом, переводом или черновиками под контролем человека.' }
      ],
      summary: 'Ваш объём', empty: 'Выберите хотя бы одно направление.', quote: 'Индивидуальный расчёт',
      quoteNote: 'Стоимость и срок сообщу после обсуждения объёма. Здесь вы ничего не заказываете.',
      currency: 'Оценка в PLN', action: 'Обсудить стоимость', messagePrefix: 'Меня интересует:'
    },
    faq: {
      label: 'Вопросы', title: 'Конкретные ответы.', search: 'Найти вопрос', noResults: 'Вопрос не найден. Напишите мне ниже.',
      items: [
        { question: 'Как формируется стоимость?', answer: 'Сначала обсуждаем цель, объём и уже используемые инструменты. Затем вы получите предложение и стоимость в PLN. Выбор опций здесь не является заказом.' },
        { question: 'Нужно ли сразу строить всю систему?', answer: 'Нет. Можно начать с одного процесса и проверить его на практике, прежде чем расширять решение.' },
        { question: 'Можно ли связать решение с нашими инструментами?', answer: 'Это проверяю в начале проекта. Возможности зависят от доступа, API и правил безопасности сервисов.' },
        { question: 'Будет ли AI принимать решения за компанию?', answer: 'AI не должен определять цену, сроки или обязательства без согласованных правил и надлежащего контроля человека.' }
      ]
    }
  }
};
